import type { ProductRepositoryMode } from './types';

/**
 * Resolve the active product repository mode from a configuration string.
 * Empty / unset uses the supplied deployment default. Invalid values throw.
 */
export function resolveProductRepositoryMode(
  raw: string | undefined | null,
  defaultMode: ProductRepositoryMode = 'local',
): ProductRepositoryMode {
  const value = (raw ?? '').trim().toLowerCase();
  if (value === '') return defaultMode;
  if (value === 'server') return 'server';
  if (value === 'local') return 'local';
  throw new Error(
    `Invalid product repository mode "${raw}". Expected "server" or "local".`,
  );
}

/**
 * Production is an unconditional device-local boundary. The legacy server
 * override exists only for deliberate development migration workflows.
 */
export function resolveProductRepositoryModeForEnvironment(
  raw: string | undefined | null,
  production: boolean,
): ProductRepositoryMode {
  if (production) return 'local';
  return resolveProductRepositoryMode(raw, 'server');
}

/**
 * Read mode from Vite client env (`VITE_PRODUCT_REPOSITORY_MODE`).
 * Public builds default to device-local persistence. Development retains the
 * legacy server repository unless explicitly configured otherwise.
 */
export function readProductRepositoryModeFromEnv(): ProductRepositoryMode {
  return resolveProductRepositoryModeForEnvironment(
    import.meta.env.VITE_PRODUCT_REPOSITORY_MODE,
    import.meta.env.PROD,
  );
}
